import { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "../hooks/useAuth";
import "./Header.css";
import claryNextLogo from "../images/Logo.svg";
import cookies from "js-cookie";
import {
  CircleUserRound,
  LogOut,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuItem,
  DropdownMenuContent,
} from "./ui/dropdown-menu";
import { config } from "../Environment";
import { WSContext } from "../App";
import { useEffectOnce } from "../hooks/useEffectOnce";
import { distributionService } from "../services/distributionService";
import MessageBox from "./MessageBox";
import Localize from "./Localize";
import useRetryFetch from "../hooks/useRetryFetch";

import LoggerContext from "../context/LoggerProvider";
import PresListContext from "../context/PresListProvider";
import { Button } from "./ui/button";
import { redirectOnLogout } from "../lib/logout";

function Header() {
  const { auth, setAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const retryFetch = useRetryFetch();

  const [showMessageBox, setShowMessageBox] = useState(false);
  const [msgBoxTitle, setMsgBoxTitle] = useState("");
  const [msgBoxButtons, setMsgBoxButtons] = useState({});
  const [msgBoxInputs, setMsgBoxInputs] = useState([]);
  const [msgBoxId, setMsgBoxId] = useState("");
  const [msgBoxMoreJSX, setMsgBoxMoreJSX] = useState(null);

  const [chatRequestUser, setChatRequestUser] = useState("");
  const [chatRequestProcessId, setChatRequestProcessId] = useState("");
  const [chatRequestRoomId, setChatRequestRoomId] = useState(0);

  const { t } = useTranslation();

  const { socket, roomId, setRoomId, globalAbortController } =
    useContext(WSContext);

  const { Logger } = useContext(LoggerContext);

  const {
    setPrescriptions,
    setPublicPrescriptions,
    setProcesses,
    setExpertProcesses,
  } = useContext(PresListContext);

  function isUser() {
    const email = auth?.email;
    const accessToken = auth?.accessToken;
    return !!email && !!accessToken;
  }

  function isAdmin() {
    const roles = auth?.roles ?? [];
    return roles.includes('sysadmin');
  }

  function isAdminPage() {
    return location.pathname === "/admin" || location.pathname?.startsWith("/users") || location.pathname === "/setup";
  }

  function isDashboardPage() {
    return location.pathname === "/portal";
  }
  function isSetupPage() {
    return location.pathname === "/setup";
  }

  // for admins who are in the admin area
  function showDashboardButton() {
    return isAdmin() && isAdminPage();
  }
  function showSetupButton() {
    return isAdmin() && isAdminPage() && !isSetupPage();
  }

  // for admins who are in the dashboard area
  function showAdminButton() {
    return isAdmin() && (isDashboardPage() || isSetupPage());
  }

  function showLoginButton() {
    return !isUser();
  }

  function showUserMenu() {
    return isUser();
  }

  const openMessageBox = (title, id, buttons, inputs, moreJSX) => {
    setMsgBoxTitle(title);
    setMsgBoxId(id);
    setMsgBoxButtons(buttons);
    setMsgBoxInputs(inputs);
    setMsgBoxMoreJSX(moreJSX);
    setShowMessageBox(true);
  };

  const onButton = async (result) => {
    Logger.debug("MessageBox button was clicked");
    Logger.debug(result);

    if (result.btnId === "yes") {
      // chat was accepted, change to ShowFullLog, join room and notify user
      socket.send(
        JSON.stringify({
          command: "acceptchat",
          payload: { email: chatRequestUser, roomId: chatRequestRoomId },
        })
      );

      // collect necessary process log information
      let response = await retryFetch(
        `${config.API_BASE}/api/processes/${chatRequestProcessId}`
      );
      let process = await response.json();

      let stateToPass = {
        processId: chatRequestProcessId,
        presName: process.prescriptionName,
        presHistoryId: process.fiPrescriptionHistory._id,
        autochat: true,
      };
      if (process.fiExpert?.email === auth.email) {
        stateToPass.userEmail = process.fiInitiator?.email;
        navigate("/review", { state: stateToPass });
      } else {
        stateToPass.expertEmail = process.fiExpert?.email;
        navigate("/review", { state: stateToPass });
      }
    } else {
      // chat was refused, send message to requesting user
      socket.send(
        JSON.stringify({
          command: "refusechat",
          payload: {
            email: chatRequestUser,
            roomId: chatRequestRoomId,
          },
        })
      );
    }
  };

  useEffectOnce(() => {

    checkCookie();

    let messageUnsubscribe = distributionService
      .getMessage()
      .subscribe(async (msg) => {
        Logger.debug("Header incoming message");

        switch (msg.command) {
          case "requestchat":
            setChatRequestUser(msg.payload.email);
            setChatRequestProcessId(msg.payload.processId);
            setChatRequestRoomId(msg.payload.roomId);

            // get process information
            let response = await retryFetch(
              `${config.API_BASE}/api/processes/${msg.payload.processId}`
            );
            let process = await response.json();

            openMessageBox(
              t("msg_chat_requested", {
                user: msg.payload.email,
              }),
              "requestchat",
              [
                { id: "yes", text: t("yes") },
                { id: "no", text: t("no") },
              ],
              [],
              <>
                <table>
                  <thead>
                    <tr>
                      <th>{t("activity_name")}</th>
                      <th>{t("status")}</th>
                      <th>{t("last_update")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{process.prescriptionName}</td>
                      <td>{t(process.status)}</td>
                      <td>{showLocale(process.lastUpdated)}</td>
                    </tr>
                  </tbody>
                </table>
                <p></p>
              </>
            );
            break;
        }
      });

    return () => {
      // cleanup subscription
      messageUnsubscribe.unsubscribe();
    };
  }, []);

  const showLocale = (ts) => {
    return new Date(ts).toLocaleString(
      cookies.get("i18next")?.split("-")[0] || "en"
    );
  };

  const navigateToAdmin = () => {
    navigate("/admin");
  };
  const navigateToSetup = () => {
    navigate("/setup");
  };

  const navigateToPortal = () => {
    navigate("/portal");
  };

  const login = () => {
    navigate("/login", {
      state: { from: location, acceptId: location.state?.acceptId },
    });
  };

  const logout = async () => {
    // Close room first
    if (roomId) {
      Logger.info("Message LEAVEROOM has been sent!");
      socket.send(
        JSON.stringify({
          command: "leaveroom",
          payload: undefined,
        })
      );
      socket.send(
        JSON.stringify({
          command: "changeuserstate",
          payload: "online",
        })
      );
      Logger.debug("HEADER: ROOM ID SET TO 0");
      setRoomId(0);
    }

    // abort with globalAbortController (mainly for loading prescription lists)
    sessionStorage.setItem("semaphore", "false");
    globalAbortController.abort();

    let response = await fetch(config.API_BASE + "/api/auth/logout", {
      credentials: "include",
    });
    Logger.debug(`logout response status ${response.status}`);

    if (!response.ok) alert(t("msg_error_logout"));

    if (response.status == 200) {
      try {
        let result = await response.json();
        Logger.debug(result);
      } catch (err) {
        Logger.debug(`failed to parse logout response`, err);
      }
    }
    setAuth({});
    localStorage.removeItem("auth"); // { email, accessToken, roles[] }

    socket.send(
      JSON.stringify({
        command: "logout",
        payload: undefined,
      })
    );

    // get rid of cache
    setPrescriptions(null);
    setPublicPrescriptions(null);
    setProcesses(null);
    setExpertProcesses(null);

    navigate(redirectOnLogout({ location }));
  };

  // const register = () => {
  //   navigate("/register");
  // };

  const checkCookie = async () => {
    Logger.debug("Checking cookie from...", location);
    try {
      let response = await fetch(`${config.API_BASE}/api/auth/checkcookie`, {
        credentials: "include",
      });

      // Check if the response has valid content type
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Invalid content-type: " + contentType);
      }

      let responseObj = await response.json(); // This might throw
      let jwtState = responseObj.result;

      if (!jwtState) {
        Logger.debug("Cookie expired. Resetting auth...");
        setAuth({});
        localStorage.removeItem("auth"); // { email, accessToken, roles[] }
        console.log("pathname", location.pathname);
        // Optional redirect logic here
      } else {
        Logger.debug("Cookie still valid.");
      }
    } catch (error) {
      console.error("checkCookie error:", error);
      // You might want to handle network errors or parsing errors differently
    }
  };

  function renderProfileIconUsername() {
    return (
      <div className="flex items-center gap-2 p-2">
        {/* <Avatar className="h-8 w-8">
          <AvatarImage src="/placeholder.svg?height=32&width=32" />
          <AvatarFallback>JD</AvatarFallback>
        </Avatar> */}
        <div className="h-8 w-8 rounded-full overflow-hidden">
          <img
            src={`https://ui-avatars.com/api/?name=${
              auth?.email.split("@")[0]
            }`}
            alt="Avatar"
          />
        </div>
        <div>
          {/* <p className="text-sm font-medium">Umer Pall</p> */}
          {/* text-muted-foreground */}
          <p className="text-xs text-black">
            {auth?.email || "N/A"}
          </p>
        </div>
      </div>
    );
  }

  function navigateToMyProfile() {
    navigate("/profile");
  }

  /*
            <Button onClick={logout} variant="outline">
              {t("logout")}
            </Button>
  */

  function renderMyProfileMenuItem() {
    return (
      <DropdownMenuItem onClick={navigateToMyProfile}>
        <CircleUserRound className="mr-2 h-4 w-4" />
        <span>{t("my_profile")}</span>
      </DropdownMenuItem>
    );
  }

  function renderLogoutMenuItem() {
    return (
      <DropdownMenuItem onClick={logout}>
        <LogOut className="mr-2 h-4 w-4" />
        <span>{t("logout")}</span>
      </DropdownMenuItem>
    );
  }

  function renderMenuItems() {
      return (
        <>
              {renderProfileIconUsername()}
              <DropdownMenuSeparator />
              {renderMyProfileMenuItem()}
              <DropdownMenuSeparator />
              {renderLogoutMenuItem()}
        </>
      );
  }

  return (
    <>
      <header className="z-50 sticky w-full top-0 flex h-16 items-center gap-4 border-b bg-secondary px-2 md:px-6 justify-between">
        <Button
          variant="none"
          size="img"
          className="h-full"
          onClick={navigateToPortal}
          // onClick={
          //   !isUser() && !auth?.accessToken ? goHome : navigateToPortal
          // }
        >
          <img
            className="h-full w-full"
            src={claryNextLogo}
            alt="ClaryNext logo"
            width="90"
          />
        </Button>
        { auth?.impersonatedBy ? (
          <div style={{  color: "red", "font-size": "0.8em", "background-color": "#ffeeee", position: "absolute", top: 4, left: 0, right: 0, "margin-inline": "auto", width: "50%" }}><div className="text-center" style={{ "font-weight": "bold" }}>{t("currently_impersonating")}: {auth?.email}</div></div>
        ) : ("") }
          <div className="flex flex-row items-center">
            {showSetupButton() ? (<Button onClick={navigateToSetup} variant="outline" style={{"margin-left": "5px", "margin-right": "5px"}}>{t("link_setup")}</Button>) : ("") }
            {showDashboardButton() ? (<Button onClick={navigateToPortal} variant="outline" style={{"margin-left": "5px", "margin-right": "5px"}}>{t("link_dashboard")}</Button>) : ("") }
            {showAdminButton() ? (<Button onClick={navigateToAdmin} variant="outline" style={{"margin-left": "5px", "margin-right": "5px"}}>{t("link_admin_area")}</Button>) : ("") }
          {showLoginButton() ? (
            <Button onClick={login}>{t("login")}</Button>
          ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {/* <Button variant="ghost" size="icon" className="h-8 w-8"> */}
              <div className="flex flex-col justify-center items-center space-y-[3px] cursor-pointer w-4">
                <span className="w-[5px] h-[5px] bg-gray-700 rounded-full"></span>
                <span className="w-[5px] h-[5px] bg-gray-700 rounded-full"></span>
                <span className="w-[5px] h-[5px] bg-gray-700 rounded-full"></span>
              </div>
              {/* </Button> */}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {renderMenuItems()}
            </DropdownMenuContent>
          </DropdownMenu>
          )}
          <Localize />
        </div>
      </header>
      {showMessageBox ? (
        <MessageBox
          onButton={onButton}
          buttons={msgBoxButtons}
          text={msgBoxTitle}
          inputs={msgBoxInputs}
          id={msgBoxId}
          moreJSX={msgBoxMoreJSX}
          onClose={() => setShowMessageBox(false)}
        />
      ) : (
        ""
      )}
    </>
  );
}

export default Header;
